import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useContactFileProcessor } from "../hooks/use-contact-file-processor";
import {
  CONTACT_FILE_PROCESSOR_STEPS,
  CONTACT_FILE_PROCESSOR_TABS,
} from "../constants/contacts";
import { COUNTRIES_LIST, DEFAULT_COUNTRY_CODE } from "../constants/countries";
import toastConfig from "../../../../../components/shared/constants/toaster-config";
import { SelectTagWidgetContainer } from "../../BroadcastView.styles";
import {
  ActionButton,
  ActionRow,
  BroadcastAppCard,
  Container,
  ContainerActions,
  CountrySelectActions,
  CountrySelectContainer,
  CountrySelectDescription,
  CountrySelectTitle,
  CountryPricingDetail,
  CountryPricingHeader,
  CountryPricingItem,
  DownloadExampleLink,
  DropZoneContainer,
  DropZoneDescription,
  DropZoneText,
  EmptyState,
  ExampleBox,
  ExcelIcon,
  FileInput,
  FormContainer,
  InstructionItem,
  InstructionsList,
  InstructionsPanel,
  InstructionsTitle,
  MainContent,
  ReuploadLink,
  ReviewContainer,
  SelectedCountryCode,
  SelectedCountryDisplay,
  SelectedCountryFlag,
  SelectedCountryName,
  Sidebar,
  SidebarSection,
  StatLabel,
  SummaryItem,
  SummaryLabel,
  SummaryNote,
  SummaryPanel,
  SummaryTitle,
  SummaryValue,
  Tab,
  TableCell,
  TableContainer,
  TableHeader,
  TabsContainer,
} from "../styles";
import { BackLink } from "../../Campaigns.style";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList as List } from "react-window";
import { ValidRowContact } from "./valid-row-contact";
import { InvalidRowContact } from "./invalid-row-contact";
import Button from "../../../../../components/shared/components/button/Button.style";
import { getFlagEmoji } from "../helpers/emoji";
import { CountryCombobox } from "./country-combobox";
import { downloadExampleExcel } from "../helpers/download-example-excel";

export const ContactFilesForm = ({
  handleNext,
  handleBackSkip,
  handleBack,
  updateBroadcastData,
  broadcastData,
}) => {
  const [step, setStep] = useState(
    broadcastData.contactsFile
      ? CONTACT_FILE_PROCESSOR_STEPS.REVIEW
      : CONTACT_FILE_PROCESSOR_STEPS.UPLOAD,
  );
  const [activeTab, setActiveTab] = useState(CONTACT_FILE_PROCESSOR_TABS.VALID);
  const [editingId, setEditingId] = useState(null);
  const [defaultCountry, setDefaultCountry] = useState(DEFAULT_COUNTRY_CODE);
  const [pendingFile, setPendingFile] = useState(null);

  const {
    selectedFile,
    isProcessing,
    validContacts,
    invalidContacts,
    countryStats,
    campaignCostData,
    error: processorError,
    pendingDuplicates,
    handleFileChange,
    handleReset,
    handleEditContact,
    downloadInvalidContacts,
    downloadValidContacts,
    deleteContact,
    resolveDuplicates,
  } = useContactFileProcessor({
    defaultCountry,
    defaultContactsFile: broadcastData.contactsFile,
    defaultContacts: broadcastData.contacts,
    defaultInvalidContacts: broadcastData.invalidContacts,
    defaultVariables: broadcastData.templateVariables,
    defaultCampaignCost: broadcastData.campaignCost,
    templateCategory: broadcastData.templateCategory,
    onReupload: () => {
      setStep(CONTACT_FILE_PROCESSOR_STEPS.UPLOAD);
      setPendingFile(null);
    },
    onSuccessfullyProcessedContacts: () => {
      setStep(CONTACT_FILE_PROCESSOR_STEPS.REVIEW);
    },
  });

  const handleFileSelect = (e) => {
    const file = e.target.files[0];

    if (file) {
      setPendingFile(file);
      setStep(CONTACT_FILE_PROCESSOR_STEPS.COUNTRY_SELECT);
    }
  };

  const handleCountryConfirm = () => {
    if (pendingFile) {
      handleFileChange(pendingFile);
      setPendingFile(null);
    }
  };

  const handleCountryBack = () => {
    setStep(CONTACT_FILE_PROCESSOR_STEPS.UPLOAD);
    setPendingFile(null);
  };

  useEffect(() => {
    if (pendingDuplicates && pendingDuplicates.length > 0) {
      setStep(CONTACT_FILE_PROCESSOR_STEPS.DUPLICATES_DECISION);
    }
  }, [pendingDuplicates]);

  const getSelectedCountry = () => {
    return COUNTRIES_LIST.find((c) => c.nameCode === defaultCountry);
  };

  const handleContinue = () => {
    if (validContacts.length === 0) {
      toast.error("No hay contactos válidos para continuar", toastConfig);
      return;
    }

    updateBroadcastData({
      contactsFile: selectedFile,
      contacts: validContacts,
      invalidContacts: invalidContacts,
      campaignCostData,
      campaignCost: campaignCostData?.totalCost || 0,
    });

    handleNext();
  };

  const handleOnBack = () => {
    updateBroadcastData({
      contactsFile: null,
      contacts: [],
      invalidContacts: [],
      campaignCostData: null,
      campaignCost: 0,
    });

    if (!broadcastData.buttons || broadcastData.buttons?.length === 0) {
      handleBackSkip();
      return;
    }

    handleBack();
  };

  // --- VIEW UPLOAD ---
  if (step === CONTACT_FILE_PROCESSOR_STEPS.UPLOAD) {
    return (
      <SelectTagWidgetContainer>
        <BroadcastAppCard>
          <InstructionsPanel>
            <InstructionsTitle>Formato del archivo</InstructionsTitle>

            <InstructionsList>
              <InstructionItem>
                <strong>Columna A:</strong> Teléfono (ej: 525512345678)
              </InstructionItem>

              <InstructionItem>
                <strong>Columnas B, C...:</strong> Variables en orden
              </InstructionItem>

              <InstructionItem>
                Es importante agregar el código de país al inicio del número de
                teléfono
              </InstructionItem>

              <InstructionItem>Máximo 5MB (.xlsx, .xls, .csv)</InstructionItem>
            </InstructionsList>

            <ExampleBox>
              Teléfono | Var 1 | Var 2<br />
              525512345678 | Juan | Pedido #123
              <br />
              525512345678 | Maria | Pedido #456
            </ExampleBox>

            <DownloadExampleLink
              type="button"
              onClick={() =>
                downloadExampleExcel(broadcastData.templateVariables)
              }
            >
              📥 Descargar plantilla de ejemplo
            </DownloadExampleLink>
          </InstructionsPanel>

          <Container>
            <FormContainer>
              <DropZoneContainer disabled={isProcessing}>
                <ExcelIcon />

                <DropZoneText>
                  {isProcessing
                    ? "Procesando..."
                    : "Arrastra tu archivo o haz clic"}
                </DropZoneText>

                <DropZoneDescription>.xlsx .xls .csv</DropZoneDescription>

                <FileInput
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileSelect}
                  disabled={isProcessing}
                />
              </DropZoneContainer>
            </FormContainer>
          </Container>

          <ContainerActions>
            <BackLink onClick={handleOnBack}>← Atrás</BackLink>
          </ContainerActions>
        </BroadcastAppCard>
      </SelectTagWidgetContainer>
    );
  }

  // --- VIEW COUNTRY SELECT ---
  if (step === CONTACT_FILE_PROCESSOR_STEPS.COUNTRY_SELECT) {
    const selectedCountry = getSelectedCountry();

    return (
      <SelectTagWidgetContainer>
        <BroadcastAppCard>
          <CountrySelectContainer>
            <CountrySelectTitle>
              Selecciona el país por defecto
            </CountrySelectTitle>

            <CountrySelectDescription>
              Los números de teléfono sin código de país se procesarán usando el
              código del país seleccionado.
            </CountrySelectDescription>

            {selectedCountry && (
              <SelectedCountryDisplay>
                <SelectedCountryFlag>
                  {getFlagEmoji(selectedCountry.nameCode)}
                </SelectedCountryFlag>

                <SelectedCountryName>
                  {selectedCountry.country}
                </SelectedCountryName>

                <SelectedCountryCode>
                  +{selectedCountry.code}
                </SelectedCountryCode>
              </SelectedCountryDisplay>
            )}

            <CountryCombobox
              value={defaultCountry}
              onChange={setDefaultCountry}
              countries={COUNTRIES_LIST}
            />

            <CountrySelectActions>
              <BackLink onClick={handleCountryBack}>← Atrás</BackLink>

              <Button designV4 onClick={handleCountryConfirm}>
                Continuar
              </Button>
            </CountrySelectActions>
          </CountrySelectContainer>
        </BroadcastAppCard>
      </SelectTagWidgetContainer>
    );
  }

  // --- VIEW DUPLICATES DECISION ---
  if (step === CONTACT_FILE_PROCESSOR_STEPS.DUPLICATES_DECISION) {
    const count = pendingDuplicates ? pendingDuplicates.length : 0;
    const title =
      count === 1
        ? "Se encontró 1 número duplicado"
        : `Se encontraron ${count} números duplicados`;

    return (
      <SelectTagWidgetContainer>
        <BroadcastAppCard>
          <CountrySelectContainer>
            <CountrySelectTitle>{title}</CountrySelectTitle>

            <CountrySelectDescription>
              Estos números aparecen más de una vez en el archivo. Puede
              conservarlos en el envío o eliminarlos; si los elimina, aparecerán
              en la lista de errores.
            </CountrySelectDescription>

            <CountrySelectActions>
              <BackLink onClick={() => resolveDuplicates(false)}>
                Eliminar duplicados
              </BackLink>

              <Button designV4 onClick={() => resolveDuplicates(true)}>
                Conservar duplicados
              </Button>
            </CountrySelectActions>
          </CountrySelectContainer>
        </BroadcastAppCard>
      </SelectTagWidgetContainer>
    );
  }

  // --- VISTA DE REVISIÓN (REVIEW) ---
  return (
    <SelectTagWidgetContainer>
      <BroadcastAppCard>
        <ActionRow>
          <h3 style={{ margin: 0, fontSize: "1.25rem" }}>
            Resultados del archivo: {selectedFile?.name}
          </h3>

          <ReuploadLink onClick={handleReset}>Subir otro archivo</ReuploadLink>
        </ActionRow>

        <ReviewContainer>
          {/* CONTENIDO PRINCIPAL: TABLAS */}
          <MainContent>
            <TabsContainer>
              <Tab
                active={activeTab === CONTACT_FILE_PROCESSOR_TABS.VALID}
                onClick={() => setActiveTab(CONTACT_FILE_PROCESSOR_TABS.VALID)}
              >
                Válidos ({validContacts.length})
              </Tab>

              <Tab
                active={activeTab === CONTACT_FILE_PROCESSOR_TABS.INVALID}
                onClick={() =>
                  setActiveTab(CONTACT_FILE_PROCESSOR_TABS.INVALID)
                }
                style={{
                  color:
                    invalidContacts.length > 0 &&
                    activeTab !== CONTACT_FILE_PROCESSOR_TABS.INVALID
                      ? "#d32f2f"
                      : undefined,
                }}
              >
                Con Errores ({invalidContacts.length})
              </Tab>

              <div
                style={{ marginLeft: "auto", display: "flex", gap: "0.5rem" }}
              >
                {activeTab === CONTACT_FILE_PROCESSOR_TABS.INVALID &&
                  invalidContacts.length > 0 && (
                    <ActionButton
                      onClick={downloadInvalidContacts}
                      style={{ border: "1px solid #d32f2f", color: "#d32f2f" }}
                    >
                      Descargar Errores
                    </ActionButton>
                  )}

                {activeTab === CONTACT_FILE_PROCESSOR_TABS.VALID &&
                  validContacts.length > 0 && (
                    <ActionButton
                      onClick={downloadValidContacts}
                      style={{ border: "1px solid #128C7E", color: "#128C7E" }}
                    >
                      Descargar Válidos
                    </ActionButton>
                  )}
              </div>
            </TabsContainer>

            <TableContainer>
              <AutoSizer>
                {({ height, width }) => (
                  <>
                    {activeTab === CONTACT_FILE_PROCESSOR_TABS.VALID &&
                      (validContacts.length > 0 ? (
                        <>
                          <TableHeader style={{ width }}>
                            <TableCell flex={1.5}>
                              Teléfono (Editable)
                            </TableCell>

                            {broadcastData.templateVariables.map((_, index) => (
                              <TableCell key={index} flex={1}>
                                Variable {index + 1}
                              </TableCell>
                            ))}

                            <TableCell flex={0.8}></TableCell>
                          </TableHeader>

                          <List
                            height={height - 50}
                            itemCount={validContacts.length}
                            itemSize={50}
                            width={width}
                            itemData={{
                              items: validContacts,
                              onEdit: handleEditContact,
                              onDelete: deleteContact,
                              editingId,
                              setEditingId,
                            }}
                          >
                            {ValidRowContact}
                          </List>
                        </>
                      ) : (
                        <EmptyState style={{ width, height }}>
                          No hay contactos válidos.
                        </EmptyState>
                      ))}

                    {activeTab === CONTACT_FILE_PROCESSOR_TABS.INVALID &&
                      (invalidContacts.length > 0 ? (
                        <>
                          <TableHeader style={{ width }}>
                            <TableCell flex={0.5}>Fila</TableCell>

                            <TableCell flex={1.5}>
                              Teléfono (Editable)
                            </TableCell>

                            <TableCell flex={2}>Error</TableCell>

                            {broadcastData.templateVariables.map((_, index) => (
                              <TableCell key={index} flex={1}>
                                Variable {index + 1}
                              </TableCell>
                            ))}

                            <TableCell flex={0.8}></TableCell>
                          </TableHeader>

                          <List
                            height={height - 50}
                            itemCount={invalidContacts.length}
                            itemSize={50}
                            width={width}
                            itemData={{
                              items: invalidContacts,
                              onEdit: handleEditContact,
                              onDelete: deleteContact,
                              editingId,
                              setEditingId,
                              defaultVariables: broadcastData.templateVariables,
                            }}
                          >
                            {InvalidRowContact}
                          </List>
                        </>
                      ) : (
                        <EmptyState style={{ width, height }}>
                          No hay contactos válidos.
                        </EmptyState>
                      ))}
                  </>
                )}
              </AutoSizer>
            </TableContainer>
          </MainContent>

          {/* SIDEBAR DE RESUMEN */}
          <Sidebar>
            <SummaryPanel>
              <SummaryTitle>Resumen</SummaryTitle>

              <SummaryItem>
                <SummaryLabel>Total procesado:</SummaryLabel>

                <SummaryValue>
                  {validContacts.length + invalidContacts.length}
                </SummaryValue>
              </SummaryItem>

              <SummaryItem>
                <SummaryLabel style={{ color: "#d32f2f" }}>
                  Con errores:
                </SummaryLabel>

                <SummaryValue style={{ color: "#d32f2f" }}>
                  {invalidContacts.length}
                </SummaryValue>
              </SummaryItem>

              <SummaryItem>
                <SummaryLabel style={{ color: "#128C7E" }}>
                  Válidos:
                </SummaryLabel>

                <SummaryValue style={{ color: "#128C7E" }}>
                  {validContacts.length}
                </SummaryValue>
              </SummaryItem>

              <SidebarSection style={{ padding: "0.75rem 0" }}>
                <SummaryLabel style={{ display: "block" }}>
                  Desglose por País
                </SummaryLabel>

                <div
                  style={{
                    maxHeight: "200px",
                    overflowY: "auto",
                    paddingRight: "4px",
                    paddingTop: "10px",
                    paddingBottom: "10px",
                    WebkitMaskImage:
                      "linear-gradient(to bottom, black 85%, transparent 100%), linear-gradient(to top, black 85%, transparent 100%)",
                    WebkitMaskComposite: "source-in",
                    maskImage:
                      "linear-gradient(to bottom, black 85%, transparent 100%), linear-gradient(to top, black 85%, transparent 100%)",
                    maskComposite: "intersect",
                  }}
                >
                  {campaignCostData?.countryBreakdowns ? (
                    Object.entries(campaignCostData.countryBreakdowns).map(
                      ([countryCode, breakdown]) => (
                        <CountryPricingItem key={countryCode}>
                          <CountryPricingHeader>
                            <StatLabel>
                              <span>{getFlagEmoji(countryCode)}</span>

                              <span>
                                {countryCode} — {breakdown.quantity} msgs
                              </span>
                            </StatLabel>

                            <SummaryValue>
                              ${(breakdown.gross ?? breakdown.net)?.toFixed(2)}{" "}
                              USD
                            </SummaryValue>
                          </CountryPricingHeader>

                          <CountryPricingDetail>
                            ${breakdown.unitPrice} / msg
                          </CountryPricingDetail>
                        </CountryPricingItem>
                      ),
                    )
                  ) : countryStats.length > 0 ? (
                    countryStats.map((country) => (
                      <CountryPricingItem key={country.country}>
                        <CountryPricingHeader>
                          <StatLabel>
                            <span>{getFlagEmoji(country.country)}</span>

                            <span>{country.country}</span>
                          </StatLabel>

                          <SummaryValue>{country.quantity}</SummaryValue>
                        </CountryPricingHeader>
                      </CountryPricingItem>
                    ))
                  ) : (
                    <span style={{ fontSize: "0.8rem", color: "#9ca3af" }}>
                      Sin datos de países
                    </span>
                  )}
                </div>
              </SidebarSection>

              <SummaryItem noBorder>
                <SummaryLabel>
                  {campaignCostData?.provider === "TP"
                    ? "Comisión por servicio:"
                    : "Costo estimado:"}
                </SummaryLabel>

                <SummaryValue large bold>
                  ${campaignCostData?.totalCost?.toFixed(2) ?? "0.00"} USD
                </SummaryValue>
              </SummaryItem>

              {campaignCostData?.provider === "TP" && (
                <SummaryNote>
                  El costo de los mensajes será cobrado directamente por Meta.
                  Podrás consultarlo en tu panel de Meta Business.
                </SummaryNote>
              )}
            </SummaryPanel>
          </Sidebar>
        </ReviewContainer>

        <ContainerActions>
          <BackLink onClick={handleOnBack} disabled={processorError}>
            ← Atrás
          </BackLink>

          <Button
            designV4
            onClick={handleContinue}
            disabled={validContacts.length === 0 || processorError}
          >
            {invalidContacts.length > 0 && validContacts.length > 0
              ? "Continuar de todas formas"
              : "Continuar"}
          </Button>
        </ContainerActions>
      </BroadcastAppCard>
    </SelectTagWidgetContainer>
  );
};
